import { useCallback, useEffect, useMemo, useState } from "react";
import { useActions } from "app/utils/hooks";
import Fuse from "../../../../vendor/fuse";
import { GroupSelectProps } from "./index.types";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  locations,
  groupObj,
  applyTemplateToGroup,
} from "app/actions/applyTemplate";
import { fetchTeams } from "app/actions/team";
import { RootState } from "typedefs";
import TeamModel from "app/models/TeamModel";

const initGroups: TeamModel[] = [];

const fuseOptions = {
  keys: ["name"],
  includeScore: true,
  shouldSort: true,
  threshold: 0.5,
};

const useTeamSelectModal = ({ onClose, templateId }: GroupSelectProps) => {
  const dispatch = useDispatch();

  const team = useSelector((state: RootState) => state.team, shallowEqual);

  const teamIds = useSelector(
    (state: RootState) => state.applyTemplate.locations,
    shallowEqual,
  );
  const teamsArr = useSelector(
    (state: RootState) => state.applyTemplate.groupObj,
    shallowEqual,
  );

  const applyTemplateActions = useActions({
    applyTemplateToGroup,
  });
  const teamActions = useActions({
    fetchTeams,
  });
  const [searchTerm, setSearch] = useState<string>("");
  const [teams, setTeams] = useState(initGroups);
  const [selectedTeamsId, setSelectedTeamsId] = useState<string[]>(teamIds);
  const [selectedTeam, setSelectedTeam] = useState<TeamModel[]>(teamsArr);

  // The modal is conditionally rendered, so mount = open: fetch here instead
  // of on page load. Keep the same query shape as ChannelSelectModal — both
  // write to the shared team slice, and un-aborted overlapping responses
  // with different shapes would clobber each other.
  useEffect(() => {
    teamActions.fetchTeams({ "options[include]": ["team_groups"] });
  }, []);

  useEffect(() => {
    if (team.data != null) {
      setTeams(team.data || []);
    }
  }, [team.data]);

  const fuse = useMemo(() => {
    return new Fuse(teams, fuseOptions);
  }, [teams]);

  const results = useMemo(() => {
    if (searchTerm !== "") {
      return fuse.search(searchTerm).map((result) => result.item);
    } else {
      return teams;
    }
  }, [searchTerm, teams, fuse]);

  const onSearchTeam = useCallback((value: string) => {
    if (value != null && value.length > 0) {
      setSearch(value);
    } else {
      setSearch("");
    }
  }, []);

  const teamSelectHandler = useCallback(
    (value: string, groupObj: TeamModel) => {
      setSelectedTeamsId((prevSelectedTeamsId) => {
        if (prevSelectedTeamsId.includes(value)) {
          return prevSelectedTeamsId.filter((val) => val !== value);
        } else {
          return [...prevSelectedTeamsId, value];
        }
      });
      // Toggle by id, not object identity: the team objects are rebuilt
      // whenever the team list is refetched, and `selectedTeam` is seeded from
      // Redux, so identity comparison would desync it from `selectedTeamsId`
      // and accumulate duplicates.
      setSelectedTeam((prevSelectedTeam) => {
        if (prevSelectedTeam.some((selected) => selected.id === value)) {
          return prevSelectedTeam.filter((selected) => selected.id !== value);
        } else {
          return [...prevSelectedTeam, groupObj];
        }
      });
    },
    [],
  );

  useEffect(() => {
    dispatch(locations(selectedTeamsId));
    dispatch(groupObj(selectedTeam));
  }, [selectedTeamsId, selectedTeam]);

  const cancelHandler = () => {
    setSelectedTeamsId([]);
    setSelectedTeam([]);
    setSearch("");
    onClose();
  };

  const applyHandler = () => {
    applyTemplateActions.applyTemplateToGroup(selectedTeamsId, templateId);
    setSearch("");
    onClose();
  };

  return {
    teamSelectHandler,
    cancelHandler,
    applyHandler,
    onSearchTeam,
    team,
    searchTerm,
    results,
    selectedTeamsId,
  };
};

export default useTeamSelectModal;
